import { useCallback, useMemo } from "react";
import {
  type BuyerParticipationRequestDetailDto,
  type BuyerQualificationReviewResponsePatchDto,
  useUpdateParticipationRequestReviewMutation,
} from "../../../../../autogen/bff-api";
import { useCreateEditor } from "../../../../../common/editing/save-coordinator/useCreateEditor";
import { useEditor } from "../../../../../common/editing/save-coordinator/useEditor";
import { useApiError } from "../../../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

interface ReviewState extends Record<string, unknown> {
  verdicts: Record<string, boolean | null>;
  comments: Record<string, string>;
}

interface UseEditor {
  state: ReviewState;
  setVerdict: (criterionKey: string, value: boolean | null) => void;
  setComment: (criterionKey: string, value: string) => void;
  isSaving: boolean;
}

/**
 * Per-criterion verdicts and comments, on the save coordinator so a refetch triggered by one save
 * cannot land on top of what is being typed. The two maps are plain objects so the merge patch
 * recurses into them and a save touches one criterion.
 */
export const useRequestQualificationReviewEditor = (
  eventId: string,
  requestId: string,
  detail: BuyerParticipationRequestDetailDto,
): UseEditor => {
  const { trigger } = useApiError();
  const [update] = useUpdateParticipationRequestReviewMutation();
  const entityId = `request-review:${requestId}`;

  const responses = detail.request.qualificationResponses;
  const initialState = useMemo(() => toReviewState(responses), [responses]);

  const save = useCallback(
    async (fields: Partial<ReviewState>): Promise<ReviewState> => {
      const updated = await update({
        eventId,
        requestId,
        buyerQualificationReviewPatchDto: { responses: toPatchResponses(fields) },
      }).unwrap();
      return toReviewState(updated.request.qualificationResponses);
    },
    [eventId, requestId, update],
  );

  useCreateEditor<ReviewState>(entityId, { initialState, save, onError: trigger });
  const { state, updateField, entityStatus } = useEditor<ReviewState>(entityId);

  const setVerdict = useCallback(
    (criterionKey: string, value: boolean | null) =>
      updateField({ verdicts: { [criterionKey]: value } }),
    [updateField],
  );

  // Typing debounces; a verdict is a single click and goes at once.
  const setComment = useCallback(
    (criterionKey: string, value: string) =>
      updateField({ comments: { [criterionKey]: value } }, { debounceMs: DEBOUNCE_MS }),
    [updateField],
  );

  return { state, setVerdict, setComment, isSaving: entityStatus === "saving" };
};

const toReviewState = (
  responses: BuyerParticipationRequestDetailDto["request"]["qualificationResponses"],
): ReviewState => {
  const verdicts: Record<string, boolean | null> = {};
  const comments: Record<string, string> = {};
  for (const response of responses ?? []) {
    verdicts[response.criterionKey] = response.buyerVerdict ?? null;
    comments[response.criterionKey] = response.buyerComment ?? "";
  }
  return { verdicts, comments };
};

// Merge patch: an untouched verdict must stay absent rather than be re-sent as null.
const toPatchResponses = (fields: Partial<ReviewState>): BuyerQualificationReviewResponsePatchDto[] => {
  const byKey = new Map<string, BuyerQualificationReviewResponsePatchDto>();
  const responseFor = (criterionKey: string) => {
    const existing = byKey.get(criterionKey) ?? { criterionKey };
    byKey.set(criterionKey, existing);
    return existing;
  };

  for (const [criterionKey, verdict] of Object.entries(fields.verdicts ?? {})) {
    byKey.set(criterionKey, { ...responseFor(criterionKey), buyerVerdict: verdict });
  }
  for (const [criterionKey, comment] of Object.entries(fields.comments ?? {})) {
    byKey.set(criterionKey, { ...responseFor(criterionKey), buyerComment: comment });
  }
  return Array.from(byKey.values());
};
